import { contentTypeNameSchema } from "@content-types"
import { requireDashboardOrigin } from "@playbrain/auth/server/dashboardOriginGuard"
import { requireDashboardAuth } from "@playbrain/auth/server/requireDashboardAuth"
import { serverRouteConfig } from "@server/server-route-config"
import { dashboardApiErrorMessage } from "@site-server/dashboard-api-errors"
import { createFileRoute } from "@tanstack/react-router"
import { createServerOnlyFn } from "@tanstack/react-start"
import * as z from "zod"

const presignInputSchema = z.object({
  contentType: contentTypeNameSchema,
  fileName: z.string().min(1).max(180),
  mimeType: z.string().min(1).max(100),
  size: z.number().int().positive(),
})

const profileImagePresignInputSchema = z.object({
  purpose: z.literal("profileImage"),
  fileName: z.string().min(1).max(180),
  mimeType: z.string().min(1).max(100),
  size: z.number().int().positive(),
})

const dashboardAssetPresignInputSchema = z.union([profileImagePresignInputSchema, presignInputSchema])

export const Route = createFileRoute("/api/dashboard/assets/presign")(
  serverRouteConfig({
    server: {
      handlers: {
        POST: async ({ request }) => {
          const originError = requireDashboardOrigin(request)
          if (originError) return originError

          try {
            const input = dashboardAssetPresignInputSchema.parse(await request.json())
            if ("purpose" in input && input.purpose === "profileImage") {
              const auth = await requireDashboardAuth(request, [])
              if ("response" in auth) return auth.response
              const { createDashboardProfileImageUploadData } = await import(
                "@api/features/content-assets/dashboardAssets"
              )
              const userId = auth.user.sub ?? auth.user.email ?? "dashboard-user"
              return Response.json(await createDashboardProfileImageUploadData({ ...input, userId }))
            }

            const auth = await requireDashboardAuth(request, [])
            if ("response" in auth) return auth.response
            try {
              await requireDashboardAssetUploadPermission(auth.user)
            } catch (error) {
              if (error instanceof Error && error.message === "PERMISSION DENIED") {
                return Response.json(
                  {
                    error:
                      "You don't have permission to upload dashboard assets. Please contact an administrator.",
                  },
                  { status: 403 }
                )
              }
              throw error
            }
            const contentInput = presignInputSchema.parse(input)
            const { createDashboardAssetUploadData } = await import(
              "@api/features/content-assets/dashboardAssets"
            )
            return Response.json(await createDashboardAssetUploadData(contentInput))
          } catch (error) {
            return Response.json(
              { error: dashboardApiErrorMessage(error, "Could not create upload URL.") },
              { status: 400 }
            )
          }
        },
      },
    },
  })
)

const requireDashboardAssetUploadPermission = createServerOnlyFn(
  async (user: {
    id?: unknown
    sub?: unknown
    role?: unknown
    user_metadata?: { role?: unknown; roles?: unknown }
  }) => {
    const { resolveDashboardUserRole } = await import(
      "@dashboard/components/users/dashboard-current-user-role"
    )
    const { assertSubjectCanUsePermission } = await import(
      "@api-users/features/admin-users/adminRolesService"
    )
    await assertSubjectCanUsePermission(
      { userId: dashboardUserId(user), role: await resolveDashboardUserRole(user) },
      "playbrain.website.assets",
      "create"
    )
  }
)

function dashboardUserId(user: { id?: unknown; sub?: unknown }) {
  if (typeof user.sub === "string" && user.sub) return user.sub
  if (typeof user.id === "string" && user.id) return user.id
  return null
}
